/**
 * SectionsEditor — Tier-3 Disclosure Engine M4 (no-deferral cleanup).
 *
 * Filterable table of `DisclosureSections` with full CRUD wired to M3:
 *   POST   /v3/admin/disclosures/sections
 *   DELETE /v3/admin/disclosures/sections/{code}
 *
 * `DisclosureSection` rows are reusable body fragments referenced by templates
 * via include directives. Per the section comment in `Data/Entities/Config/Disclosures/DisclosureSection.cs`
 * they're keyed by Code (PK) and carry: Title, DocCode, Category, Source,
 * BodyHtml, Version, IsActive.
 *
 * Bill 2026-05-08 — added after Agent G flagged sections as the only spec gap;
 * "no deferral" rule applies. SectionsEditor mirrors VariantEditor's CRUD shell.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ArrowLeft, Plus, RefreshCw, Search, Trash2, Edit3 } from '@retail-ops/panel-shell';

import { useDisclosuresConfig } from '@/contexts/DisclosuresContext';
import type { DisclosureSectionRow } from '@/services/disclosuresApi';
import {
  upsertSection,
  deleteSection,
  type DisclosureSectionUpsertInput,
} from '@/services/disclosuresAdminApi';

import {
  Checkbox,
  ConfirmDialog,
  Field,
  FieldGrid,
  Modal,
  TextInput,
  Toast,
  useToast,
} from './_shared';

interface FormShape {
  code: string;
  title: string;
  docCode: string;
  category: string;
  source: string;
  bodyHtml: string;
  version: string;
  isActive: boolean;
}

const EMPTY_FORM: FormShape = {
  code: '',
  title: '',
  docCode: '',
  category: '',
  source: '',
  bodyHtml: '',
  version: '1.0',
  isActive: true,
};

function rowToForm(r: DisclosureSectionRow | null): FormShape {
  if (!r) return EMPTY_FORM;
  const get = (k: string): string => {
    const v = (r as Record<string, unknown>)[k];
    return v === null || v === undefined ? '' : String(v);
  };
  const isActiveRaw = (r as Record<string, unknown>)['isActive'];
  return {
    code: get('code') || get('sectionKey'),
    title: get('title'),
    docCode: get('docCode') || get('templateCode'),
    category: get('category'),
    source: get('source'),
    bodyHtml: get('bodyHtml'),
    version: get('version') || '1.0',
    isActive: isActiveRaw === false ? false : true,
  };
}

function formToInput(f: FormShape): DisclosureSectionUpsertInput {
  return {
    code: f.code.trim(),
    title: f.title.trim() || null,
    docCode: f.docCode.trim() || null,
    category: f.category.trim() || null,
    source: f.source.trim() || null,
    bodyHtml: f.bodyHtml || null,
    version: f.version.trim() || null,
    isActive: f.isActive,
  };
}

export function SectionsEditor(): JSX.Element {
  const { envelope, state, error, refresh } = useDisclosuresConfig();
  const [filter, setFilter] = useState<string>('');
  const [editorOpen, setEditorOpen] = useState<boolean>(false);
  const [editing, setEditing] = useState<DisclosureSectionRow | null>(null);
  const [form, setForm] = useState<FormShape>(EMPTY_FORM);
  const [busy, setBusy] = useState<boolean>(false);
  const [inlineErr, setInlineErr] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<DisclosureSectionRow | null>(null);
  const { toast, push, clear } = useToast();

  useEffect(() => {
    if (editorOpen) {
      setForm(rowToForm(editing));
      setInlineErr(null);
    }
  }, [editorOpen, editing]);

  const rows = useMemo<ReadonlyArray<DisclosureSectionRow>>(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return envelope.sections;
    return envelope.sections.filter((s) => {
      const r = s as Record<string, unknown>;
      const haystack = [
        r['code'], r['sectionKey'], r['title'], r['docCode'], r['templateCode'],
        r['category'], r['source'],
      ]
        .map((x) => String(x ?? '').toLowerCase())
        .join('|');
      return haystack.includes(q);
    });
  }, [envelope.sections, filter]);

  const set = <K extends keyof FormShape>(key: K, value: FormShape[K]): void =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const canSave = !busy && form.code.trim() !== '';

  const submit = async (): Promise<void> => {
    if (!canSave) return;
    setBusy(true);
    setInlineErr(null);
    try {
      const result = await upsertSection(formToInput(form));
      if (!result.success) {
        const msg = result.error ?? 'Save failed.';
        setInlineErr(msg);
        push('err', msg);
        return;
      }
      push('ok', `Section ${form.code} saved.`);
      setEditorOpen(false);
      setEditing(null);
      void refresh();
    } finally {
      setBusy(false);
    }
  };

  const onDeleteConfirm = useCallback(async (): Promise<void> => {
    if (!confirmDelete) return;
    const r = confirmDelete as Record<string, unknown>;
    const code = String(r['code'] ?? r['sectionKey'] ?? '');
    if (!code) {
      setConfirmDelete(null);
      return;
    }
    setBusy(true);
    try {
      const result = await deleteSection(code);
      if (!result.success) {
        push('err', result.error ?? 'Delete failed.');
        return;
      }
      push('ok', `Section ${code} deleted.`);
      setConfirmDelete(null);
      void refresh();
    } finally {
      setBusy(false);
    }
  }, [confirmDelete, push, refresh]);

  return (
    <>
      <Toast toast={toast} onClose={clear} />

      <nav className="back-link-row" aria-label="breadcrumb">
        <Link to="/" className="back-link">Home</Link>
        <span aria-hidden="true"> / </span>
        <Link to="/disclosures" className="back-link">Disclosure Engine</Link>
        <span aria-hidden="true"> / </span>
        <span>Sections</span>
      </nav>

      <Link to="/disclosures" className="back-link">
        <ArrowLeft size={14} /> Back to Disclosure Engine
      </Link>

      <section className="page-header">
        <h1 className="page-header__title">Disclosure Sections</h1>
        <p className="page-header__subtitle page-intro">
          Reusable body fragments referenced by templates. Sections can be
          authored once and embedded into multiple disclosure bodies — useful
          for shared regulatory boilerplate (Reg DD line, e-sign consent, etc).
        </p>
      </section>

      <header
        className="card-header"
        style={{ display: 'flex', alignItems: 'center', gap: 12 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Search size={14} aria-hidden="true" />
          <TextInput
            placeholder="Filter by code, title, doc code, category…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ minWidth: 280, width: 280 }}
          />
        </div>
        <div className="toolbar__spacer" style={{ flex: 1 }} />
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => void refresh()}
          disabled={state === 'loading'}
        >
          <RefreshCw size={14} /> {state === 'loading' ? 'Refreshing…' : 'Refresh'}
        </button>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          onClick={() => {
            setEditing(null);
            setEditorOpen(true);
          }}
        >
          <Plus size={14} /> New section
        </button>
      </header>

      {error && <div className="banner banner--err" role="alert">{error}</div>}

      {state === 'ok' && rows.length === 0 && (
        <div className="empty">No sections match the current filter.</div>
      )}

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table table--striped table--hover">
            <thead>
              <tr>
                <th>Code</th>
                <th>Title</th>
                <th>DocCode</th>
                <th>Category</th>
                <th>Source</th>
                <th>Version</th>
                <th>Active</th>
                <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s, i) => {
                const r = s as Record<string, unknown>;
                const code = String(r['code'] ?? r['sectionKey'] ?? '—');
                return (
                  <tr key={`${code}-${i}`}>
                    <td><code className="cell-id">{code}</code></td>
                    <td>{r['title'] ? String(r['title']) : '—'}</td>
                    <td>{r['docCode'] ? String(r['docCode']) : (r['templateCode'] ? String(r['templateCode']) : '—')}</td>
                    <td>{r['category'] ? String(r['category']) : '—'}</td>
                    <td>{r['source'] ? String(r['source']) : '—'}</td>
                    <td>{r['version'] ? String(r['version']) : '—'}</td>
                    <td>
                      {r['isActive'] === false ? (
                        <span className="pill pill--muted">Inactive</span>
                      ) : (
                        <span className="pill pill--ok">Active</span>
                      )}
                    </td>
                    <td className="col-actions" style={{ whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => {
                          setEditing(s);
                          setEditorOpen(true);
                        }}
                      >
                        <Edit3 size={12} /> Edit
                      </button>{' '}
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setConfirmDelete(s)}
                        style={{ color: 'var(--pcb-red)' }}
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editorOpen && (
        <Modal
          open={editorOpen}
          title={editing ? `Edit Section: ${form.code || '(new)'}` : 'New Section'}
          onClose={() => {
            setEditorOpen(false);
            setEditing(null);
          }}
          width={720}
        >
          {inlineErr && (
            <div className="banner banner--err" role="alert">{inlineErr}</div>
          )}
          <FieldGrid>
            <Field label="Code" required>
              <TextInput
                value={form.code}
                onChange={(e) => set('code', e.target.value)}
                disabled={editing !== null}
                placeholder="e.g. REGDD_INTRO"
              />
            </Field>
            <Field label="Title">
              <TextInput
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                placeholder="Human-readable title"
              />
            </Field>
            <Field label="DocCode" hint="Parent template code, if section is doc-scoped">
              <TextInput
                value={form.docCode}
                onChange={(e) => set('docCode', e.target.value)}
              />
            </Field>
            <Field label="Category">
              <TextInput
                value={form.category}
                onChange={(e) => set('category', e.target.value)}
                placeholder="e.g. regulatory, marketing, footer"
              />
            </Field>
            <Field label="Source" hint="Where the body originates (e.g. ascensus, pcb-canonical)">
              <TextInput
                value={form.source}
                onChange={(e) => set('source', e.target.value)}
              />
            </Field>
            <Field label="Version">
              <TextInput
                value={form.version}
                onChange={(e) => set('version', e.target.value)}
              />
            </Field>
          </FieldGrid>
          <Field label="BodyHtml" hint="Full HTML fragment. Embedded via include directive.">
            <textarea
              aria-label="Section body HTML"
              value={form.bodyHtml}
              onChange={(e) => set('bodyHtml', e.target.value)}
              rows={14}
              style={{
                width: '100%',
                fontFamily: 'monospace',
                fontSize: 12,
                padding: 8,
                border: '1px solid var(--ink-300)',
                borderRadius: 4,
              }}
            />
          </Field>
          <Field label="Active">
            <Checkbox
              checked={form.isActive}
              onChange={(e) => set('isActive', e.target.checked)}
              label="Section is active and selectable"
            />
          </Field>
          <div className="modal__footer">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setEditorOpen(false);
                setEditing(null);
              }}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void submit()}
              disabled={!canSave}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </Modal>
      )}

      {confirmDelete && (
        <ConfirmDialog
          open={confirmDelete !== null}
          title="Delete section"
          message={`Are you sure you want to delete section ${String((confirmDelete as Record<string, unknown>)['code'] ?? (confirmDelete as Record<string, unknown>)['sectionKey'] ?? '')}? This soft-deletes (IsActive=false); the row stays in the database for audit.`}
          confirmLabel="Delete"
          confirmKind="danger"
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => void onDeleteConfirm()}
          busy={busy}
        />
      )}
    </>
  );
}
